import { useEffect, useMemo, useState } from "react";
import { MdClose, MdDelete, MdEditNote, MdPrint } from "react-icons/md";
import { TbTruckDelivery } from "react-icons/tb";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import "../delivery-runs.css";

/* ------------------------------------------------------------------ *
 * RunDetailModal — one delivery run, as a modal over the run list.
 *
 * Replaces DriverRunListDetail in components/movableDragDropArea.js:
 *   - a grey strip of unlabelled icon buttons -> a header with the
 *     driver first and named, titled actions
 *   - delete on a bare click                  -> an inline confirm strip
 *   - a fixed 800px print-shaped body         -> a scrolling stop list
 *   - RunDeliveriesListRow fetching /settings
 *     once per row for easel/pedestal add-ons -> one fetch per modal
 *
 * Presentational apart from the delete call: the caller owns which run
 * is open, and edit/print navigation. `run` is the raw /run/getAllBy
 * document. Stops merge the way legacy MergeWorkOrders merged — per
 * funeral + destination, and per work order for retail.
 * ------------------------------------------------------------------ */

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const locOf = (raw) => safeParse(raw, {}) || {};

/* Same key legacy MergeWorkOrders used: coordinates when the location
   has them, else the whitespace-stripped lowercase name. */
function stopKey(el) {
  if (el.order_type !== "Funeral") return `wo-${el.work_order_id}`;
  const loc = locOf(el.location);
  const c = loc.coordinates;
  const place = c
    ? `${c.lat}_${c.lng}`
    : String(loc.name || "").replace(/\s+/g, "").toLowerCase();
  return `${el.parent_funeral_id}@${place}`;
}

function buildStops(deliveries) {
  const byKey = new Map();
  (deliveries || []).forEach((el) => {
    const key = stopKey(el);
    if (!byKey.has(key)) byKey.set(key, { key, lead: el, products: [] });
    byKey.get(key).products.push(...safeParse(el.products, []));
  });
  return Array.from(byKey.values());
}

/* "2 x CC01" for casket covers (they carry a code), aggregated
   "3 x Wreath" for everything else — the strings the legacy run views
   showed via shorten_product_list. */
function shortenProducts(products) {
  const out = [];
  const agg = {};
  (products || []).forEach((p) => {
    const n = Number(p.amount) || 1;
    if (String(p.product_type || "").toLowerCase() === "casket cover" && p.code) {
      out.push(`${n} x ${p.code}`);
    } else {
      const key = p.product_type || p.product_name || "Product";
      agg[key] = (agg[key] || 0) + n;
    }
  });
  Object.keys(agg).forEach((k) => out.push(`${agg[k]} x ${k}`));
  return out;
}

/* Easels and pedestals ride along with certain product types, named in
   settings (easel_types / pedestal_types) — same rule as the legacy
   delivery_addons helper, but with settings fetched once per modal
   instead of once per row. */
function addOnsFor(products, settings) {
  const easelTypes = safeParse(settings.easel_types, []) || [];
  const pedestalTypes = safeParse(settings.pedestal_types, []) || [];
  let easels = 0;
  let pedestals = 0;
  (products || []).forEach((p) => {
    const n = Number(p.amount) || 0;
    if (pedestalTypes.includes(p.product_type)) pedestals += n;
    else if (easelTypes.includes(p.product_type)) easels += n;
  });
  const out = [];
  if (easels > 0) out.push(`Easels x ${easels}`);
  if (pedestals > 0) out.push(`Pedestals x ${pedestals}`);
  return out;
}

export function RunDetailModal({ run, onClose, onEdit, onDeleted }) {
  const [settings, setSettings] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status === "success") setSettings(doc.data.response[0] || {});
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stops = useMemo(() => buildStops(run.deliveries), [run]);
  const productCount = useMemo(
    () => stops.reduce((n, s) => n + s.products.length, 0),
    [stops]
  );

  function remove() {
    setDeleting(true);
    setDeleteError("");
    axios
      .post(BASE_SERVER_URL + "/run/removeRun", { run_id: run._id })
      .then((doc) => {
        setDeleting(false);
        if (doc.data.status === "success") {
          onDeleted();
        } else {
          setDeleteError("The server refused the delete. Please try again.");
        }
      })
      .catch(() => {
        setDeleting(false);
        setDeleteError("Couldn't reach the server.");
      });
  }

  const meta = [
    { k: "Delivery date", v: run.delivery_date },
    { k: "Driver", v: run.drivers_name },
    { k: "Vehicle no. plate", v: run.vehicle_number },
    { k: "Stops", v: String(stops.length) },
    { k: "Products", v: String(productCount) },
  ];

  return (
    <div className="rd-wrap" role="dialog" aria-modal="true">
      <div className="rd-scrim" onClick={onClose} />
      <div className="rd-modal">
        <header className="rd-head">
          <span className="rd-badge">
            <TbTruckDelivery />
          </span>
          <div className="rd-head-main">
            <div className="rd-eyebrow">Delivery run</div>
            <h3 className="rd-title">{run.drivers_name || "Unnamed driver"}</h3>
          </div>
          <div className="rd-actions">
            <button
              type="button"
              className="rd-icon-btn"
              title="Print run sheet"
              onClick={() => window.open("/run-sheet/print/" + run._id, "_blank")}
            >
              <MdPrint />
            </button>
            <button
              type="button"
              className="rd-icon-btn"
              title="Edit this run"
              onClick={onEdit}
            >
              <MdEditNote />
            </button>
            <button
              type="button"
              className="rd-icon-btn is-danger"
              title="Delete this run"
              onClick={() => setConfirming(true)}
            >
              <MdDelete />
            </button>
            <button
              type="button"
              className="rd-icon-btn"
              title="Close"
              onClick={onClose}
            >
              <MdClose />
            </button>
          </div>
        </header>

        {confirming && (
          <div className="rd-confirm">
            <span>
              {deleteError || (
                <>
                  Delete <b>{run.drivers_name || "this"}</b>&apos;s run for{" "}
                  <b>{run.delivery_date}</b>? Its {stops.length} stops go back to
                  unassigned.
                </>
              )}
            </span>
            <span className="rf-spacer" />
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={() => {
                setConfirming(false);
                setDeleteError("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn rd-btn-danger"
              onClick={remove}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete run"}
            </button>
          </div>
        )}

        <div className="rd-meta">
          {meta.map((m) => (
            <div className="rd-meta-cell" key={m.k}>
              <span className="rd-meta-k">{m.k}</span>
              <span className={`rd-meta-v${m.v ? "" : " is-empty"}`}>
                {m.v || "—"}
              </span>
            </div>
          ))}
        </div>

        <div className="rd-body">
          <div className="rd-sec-title">
            Stops <span>in delivery order</span>
          </div>

          <div className="rd-stops">
            <div className="rd-stop is-head">
              <span>#</span>
              <span>Name</span>
              <span>Destination</span>
              <span>Time</span>
              <span>Products</span>
            </div>

            {stops.length === 0 && <div className="rf-empty">This run has no stops.</div>}

            {stops.map((stop, i) => {
              const el = stop.lead;
              const funeral = el.order_type === "Funeral";
              const fd = funeral ? safeParse(el.fd_code, {}) || {} : {};
              const dest = locOf(el.location).name || "";
              const addOns = addOnsFor(stop.products, settings);
              return (
                <div
                  key={stop.key}
                  className={`rd-stop is-${funeral ? "funeral" : "retail"}`}
                >
                  <span className="rd-stop-num">{i + 1}</span>
                  <span>
                    <span className="rd-stop-name">{el.name || "—"}</span>
                    <span className="rd-stop-type">
                      {el.order_type || ""}
                      {el.product_type ? ` · ${el.product_type}` : ""}
                    </span>
                  </span>
                  <span className="rd-stop-dest">
                    {fd.code && <span className="rd-fd">{fd.code}</span>}
                    <span className="rd-stop-addr">{dest || "—"}</span>
                    {el.funeral_time && (
                      <span className="rd-stop-service">
                        Funeral service time {el.funeral_time}
                      </span>
                    )}
                  </span>
                  <span className={`rd-stop-time${el.time ? "" : " is-empty"}`}>
                    {el.time || "—"}
                  </span>
                  <span className="rd-prods">
                    {shortenProducts(stop.products).map((label, j) => (
                      <span className="rd-prod" key={`p-${j}`}>
                        {label}
                      </span>
                    ))}
                    {addOns.map((label, j) => (
                      <span className="rd-prod is-addon" key={`a-${j}`}>
                        {label}
                      </span>
                    ))}
                    {stop.products.length === 0 && addOns.length === 0 && "—"}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="rd-notes">
            <div className="rd-note">
              <div className="rd-note-k">Description</div>
              <div className={`rd-note-v${run.description ? "" : " is-empty"}`}>
                {run.description || "No description."}
              </div>
            </div>
            <div className="rd-note">
              <div className="rd-note-k">Comment</div>
              <div className={`rd-note-v${run.comment ? "" : " is-empty"}`}>
                {run.comment || "No comment."}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RunDetailModal;
